import React, { useCallback } from 'react';
import StripePaymentButtonElement from '../../../../../01_atoms/Payments/StripePaymentButtonElement';
import { useForm, useFormState } from 'react-final-form';
import { RG, SD } from '../../../../../../utils/constants';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { PAYMENT_MODE, PAYMENT_MODE_TEST } from '../../../../../../utils/payments';
import unexpectedErrorMessage from '../../../../../../utils/unexpectedErrorMessage';
import { showCaptchaChallenge as showCaptchaChallengeCallback } from '../../../../../../utils/captcha';

import styles from './index.module.scss';

const StripePaymentButton = ({
  orderId = null,
  captchaToken = '',
  captchaObject = null,
  paymentMethodId,
  showButton = true,
  onStripeButtonAvailabilityCheck,
  onSuccessPayment,
  onStripeButtonClick,
  onCancelPayment,
  onError,
  beforePaymentMethod,
}) => {
  const form = useForm();
  const formState = useFormState();
  const donationType = formState.values['donation_type'];
  const totalAmount = formState.values.totalAmount ?? 0;
  const amount = formState.values.fundraisingCostsApplied
    ? totalAmount
    : parseInt(formState.values.amount[donationType] || 0, 10);

  const router = useRouter();
  const forceCaptcha = parseInt(router.query.forceCaptcha, 10) === 1;
  // Making sure Captcha is activated on backend.
  // Checking if either we are forcing captcha for testing purposes
  // or we are in production.
  const showCaptchaChallenge = showCaptchaChallengeCallback.bind(null, {
    showGoogleAppleCaptcha: formState.initialValues.show_google_apple_captcha,
    showCreditCardCaptcha: formState.initialValues.show_credit_card_captcha,
  });

  const handleError = useCallback((error) => {
    onError({
      paymentMethodId,
      message: "Sorry, we couldn't complete the donation. Please try a different payment method.",
      subline: unexpectedErrorMessage(),
      additionalMessage: error.message,
    });
  });

  return (
    <div className={styles['stripe-payment-button']}>
      <StripePaymentButtonElement
        amount={amount}
        captchaToken={captchaToken}
        captchaObject={captchaObject}
        orderId={orderId}
        withCaptcha={
          showCaptchaChallenge(paymentMethodId) &&
          (forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST)
        }
        donationType={donationType === 'single' ? SD : RG}
        paymentMethodId={paymentMethodId}
        showButton={showButton}
        onSubmitDonation={() => form.submit()}
        onUnexpectedError={handleError}
        onSuccessPaymentCompleted={onSuccessPayment}
        beforePaymentMethod={beforePaymentMethod}
        onCancelPayment={onCancelPayment}
        onStripeButtonClick={onStripeButtonClick}
        onConfirmCardPaymentError={handleError}
        onStripeButtonAvailabilityCheck={onStripeButtonAvailabilityCheck}
        onConfirmCardPaymentActionError={handleError}
        buttonStyles={{
          paymentRequestButton: {
            type: 'donate',
            height: '50px',
          },
        }}
      />
    </div>
  );
};

StripePaymentButton.propTypes = {
  orderId: PropTypes.number,
  captchaToken: PropTypes.string,
  captchaObject: PropTypes.object,
  paymentMethodId: PropTypes.string.isRequired,
  showButton: PropTypes.bool,
  onStripeButtonAvailabilityCheck: PropTypes.func.isRequired,
  onSuccessPayment: PropTypes.func.isRequired,
  onStripeButtonClick: PropTypes.func.isRequired,
  onCancelPayment: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  beforePaymentMethod: PropTypes.func.isRequired,
};

export default StripePaymentButton;
